import { useState } from "react";
import classNames from "classnames";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";

import UiConfigButton from "../../atomic/UiConfigButton/UiConfigButton";
import { forceDownloadLink } from "../../../shared/utils/Global";
import { SectionActionButtonsProps, UICONFIGBUTTON } from "./types";
import Button from "src/components/atomic/Button";

export default function SectionActionButtons({
  buttonList,
  loanApplicationID,
  userID,
  token,
  tabName,
  sectionID,
  isLenderPlatform,
  graphQLResponse,
  onDataRefresh,
  pageName,
  notify,
  nestedFieldList,
  itemIndex
}: SectionActionButtonsProps) {
  const [loadingButtons, setLoadingButtons] = useState<Record<string, boolean>>(
    {}
  );

  // Helper function to resolve path expressions from graphQLResponse
  const resolvePathExpression = (path: string, data: any): string | null => {
    if (!path || !path.startsWith(".")) {
      return path; // Not a path expression, return as-is
    }

    try {
      // Remove leading dot and split by dots
      const keys = path.substring(1).split(".");
      let result: any = data;

      // Traverse the path
      for (const key of keys) {
        if (result === null || result === undefined) {
          return null;
        }
        result = result[key];
      }

      return typeof result === "string" ? result : null;
    } catch (error) {
      console.error("Error resolving path expression:", path, error);
      return null;
    }
  };

  const handleButtonAction = async (
    buttonKey: string,
    url: string,
    buttonLabel?: string
  ) => {
    // Resolve path expression if URL starts with a dot
    let resolvedUrl = url;
    if (url?.startsWith(".") && graphQLResponse) {
      resolvedUrl = resolvePathExpression(url, graphQLResponse) || url;
    }

    if (!resolvedUrl) {
      notify?.({
        title: "Error",
        text: "Download URL is not available",
        type: "error"
      });
      return;
    }
    setLoadingButtons((prev) => ({ ...prev, [buttonKey]: true }));

    try {
      const urlParts = resolvedUrl.split("/");
      const urlFileName = urlParts[urlParts.length - 1];

      // Remove query parameters if any
      const cleanFileName = urlFileName?.split("?")[0];
      const fileName =
        cleanFileName && cleanFileName.endsWith(".pdf")
          ? cleanFileName
          : `${buttonLabel || "document"}.pdf`;

      await new Promise((resolve) => setTimeout(resolve, 300));

      await forceDownloadLink(resolvedUrl, fileName);

      notify?.({
        title: "Success",
        text: `${buttonLabel || "File"} downloaded successfully`,
        type: "success"
      });
    } catch (error: any) {
      console.error("Failed to download file:", error);
      notify?.({
        title: "Download Failed",
        text:
          error?.message ||
          "Failed to download the file. Please try again later.",
        type: "error"
      });
    } finally {
      setLoadingButtons((prev) => ({ ...prev, [buttonKey]: false }));
    }
  };

  // Extract client-side inputs from nestedFieldList for function_call actions
  const extractClientSideInputs = () => {
    if (!nestedFieldList || !loanApplicationID || !token || !userID) {
      return {
        accountNumber: "",
        ifscCode: "",
        userID: userID || "",
        loanApplicationID: loanApplicationID || "",
        token: token || "",
        accountType: ""
      };
    }

    const fieldValue = (id: string) =>
      nestedFieldList?.find((f) => f.id === id)?.value || "";

    const accountNumber =
      nestedFieldList?.find((f) => f.id === "account_number")?.value || "";
    const userId =
      graphQLResponse?.bankDetails?.find(
        (f) => f.accountNumber === accountNumber
      )?.userID || userID;

    return {
      accountNumber: fieldValue("account_number"),
      ifscCode: fieldValue("ifsc_code"),
      userID: userId,
      loanApplicationID: loanApplicationID || "",
      token: token || "",
      accountType: fieldValue("account_type"),
      ...(tabName === "pennydrop_tab" && {
        bankName: graphQLResponse?.bankDetails?.[itemIndex]?.bankName,
        userBankDetailsID:
          graphQLResponse?.bankDetails?.[itemIndex]?.userBankDetailsID
      })
    };
  };

  const visibleButtons = Object.entries(buttonList).filter(
    ([_, button]: [string, any]) => button?.visible?.isVisible === true
  );

  if (visibleButtons.length === 0) return null;

  return (
    <div className="flex items-center justify-end gap-2 mb-4 ml-2">
      {visibleButtons.map(([buttonKey, button]: [string, any]) => {
        const actionType = button?.action?.type;
        if (
          actionType === "function_call" ||
          (actionType === "api_call" && tabName === "pennydrop_tab")
        ) {
          if (!loanApplicationID) {
            console.warn(
              "loanApplicationID is required for function_call buttons"
            );
            return null;
          }

          return (
            <UiConfigButton
              key={buttonKey}
              buttonConfig={button}
              clientSideInput={extractClientSideInputs()}
              loanApplicationID={loanApplicationID}
              tabName={tabName || ""}
              isLenderDashboard={isLenderPlatform || false}
              onClickFinished={onDataRefresh}
              onPostClick={onDataRefresh}
              sectionID={sectionID}
              styles={{
                className: "ml-0 text-sm",
                size: "small"
              }}
            />
          );
        }

        if (
          actionType === UICONFIGBUTTON.API_CALL &&
          tabName !== "pennydrop_tab"
        ) {
          const getIcon = () => {
            switch (button?.icon?.toLowerCase()) {
              case "download":
                return <ArrowDownTrayIcon className="w-4 h-4" />;
              default:
                return null;
            }
          };
          const isLoading = loadingButtons[buttonKey];
          const buttonUrl = button?.action?.params?.endpoint;

          return (
            <Button
              key={buttonKey}
              type="button"
              onClick={() => {
                if (buttonUrl) {
                  handleButtonAction(
                    buttonKey,
                    buttonUrl,
                    button?.hoverString || button?.label
                  );
                }
              }}
              disabled={isLoading || !buttonUrl}
              className={classNames(
                "flex items-center gap-2 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors",
                isLoading || !buttonUrl
                  ? "text-gray-400 border-gray-300 cursor-not-allowed bg-gray-50"
                  : "text-indigo-700 border-indigo-700 hover:bg-indigo-50"
              )}
              title={button?.hoverString || button?.label}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  {getIcon()}
                  {button?.label && <span>{button.label}</span>}
                </>
              )}
            </Button>
          );
        }

        return null;
      })}
    </div>
  );
}
